import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
  effect,
} from "@angular/core";
import { WorkflowUpdatesDto } from "../../../core/models/project-workflow/workflowUpdatesDto";
import { WorkflowUpdatesPinRequestDto } from "../../../core/models/project-workflow/workflowUpdatesPinRequestDto";
import { WorkflowUpdatesResponseDto } from "../../../core/models/project-workflow/workflowUpdatesResponseDto";
import { WorkflowNotesDto } from "../../../core/models/project-workflow/workflowNotesDto";
import { WorkflowNotesResponseDto } from "../../../core/models/project-workflow/workflowNotesResponseDto";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { SignalRService } from "../../../core/services/signal-r.service";
import { MsalApiService } from "../../../core/services/msal-api.service";
import { DomSanitizer } from "@angular/platform-browser";
import { forkJoin, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { TabsModule } from "primeng/tabs";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import { CheckboxChangeEvent, CheckboxModule } from "primeng/checkbox";
import { EditorModule } from "primeng/editor";
import { TooltipModule } from "primeng/tooltip";
import { AvatarModule } from "primeng/avatar";
import { NameInitialsPipe } from "../../../shared/pipes/name-initials/name-initials.pipe";

@Component({
  selector: "jni-workflow-updates",
  templateUrl: "./workflow-updates.component.html",
  styleUrls: ["./workflow-updates.component.scss"],
  imports: [
    FormsModule,
    CommonModule,
    TabsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    TextareaModule,
    CheckboxModule,
    EditorModule,
    TooltipModule,
    AvatarModule,
    NameInitialsPipe,
  ],
})
export class WorkflowUpdatesComponent implements OnInit, AfterViewInit {
  @Input() projectWorkflowId = 0;
  @Input() isWorkflowOwners = true;
  @Input() isDisableLink = false;
  update = "";
  readonly noteEditorStyle = { height: "3rem" };
  workflowUpdatesList = [] as WorkflowUpdatesResponseDto[];
  workflowUpdatesPinnedList = [] as WorkflowUpdatesResponseDto[];
  note = "";
  workflowNotesList = [] as WorkflowNotesResponseDto[];
  workflowNotesPinnedList = [] as WorkflowNotesResponseDto[];
  photoCache = new Map<string, string>();
  @Output() showSmalLoaderEvent = new EventEmitter<boolean>();
  @Output() hasUnsavedChangesEvent = new EventEmitter<boolean>();

  @ViewChild("updatesContainer") updatesContainer!: ElementRef;

  personnelNumber = "";
  pinned = false;
  activeIndex = 0;

  onTabChange(value: number | string): void {
    this.activeIndex = Number(value);
  }

  constructor(
    private _productionWorkflowService: ProjectWorkflowService,
    private _toastr: JniToastrService,
    private _signalRService: SignalRService,
    private _msalApiService: MsalApiService,
    private _domSanitizer: DomSanitizer
  ) {
    effect(() => {
      const updates = this._signalRService.updates();
      if (updates) {
        this.getWorkflowUpdatesList();
      }
    });
  }

  ngOnInit() {
    this.getWorkflowUpdatesList();
    this.getWorkflowNotesList();
  }

  ngAfterViewInit() {
    this.scrollToBottom();
  }

  postWorkflowUpdate() {
    this.showSmalLoaderEvent.emit(true);
    const payload = {} as WorkflowUpdatesDto;
    if (!this.update || this.update === "") {
      this._toastr.error("Error", "Enter a message to post Updates");
      this.showSmalLoaderEvent.emit(false);
      return;
    }
    payload.message = this.convertLinksToAnchorTags(this.update);
    payload.projectWorkFlowId = this.projectWorkflowId;
    this._productionWorkflowService.postWorkflowUpdates(payload).subscribe({
      next: res => {
        if (res?.status !== "Success") {
          this._toastr.error("Error", "Update was not saved");
        }
        this.update = "";
        this.scrollToBottom();
        this.getWorkflowUpdatesList();
        this.hasUnsavedChangesEvent.emit(false);
      },
      error: res => {
        this._toastr.error("Error", res.message);
        this.showSmalLoaderEvent.emit(false);
      },
    });
  }

  getWorkflowUpdatesList(scroll = true) {
    if (this.projectWorkflowId !== 0) {
      this._productionWorkflowService
        .getWorkflowUpdatesList(this.projectWorkflowId)
        .subscribe({
          next: res => {
            if (res?.totalRecords && res.totalRecords > 0) {
              this.workflowUpdatesList = res.data;
              this.workflowUpdatesPinnedList = res.data.filter(x => x.pinned);
              this.loadUserPhotos(res.data);
              if (scroll) {
                setTimeout(() => this.scrollToBottom(), 0);
              }
            }

            this.showSmalLoaderEvent.emit(false);
          },
          error: res => {
            this._toastr.error("Error", res.message);
            this.showSmalLoaderEvent.emit(false);
          },
        });
    } else {
      this.workflowUpdatesList = [];
    }
  }

  scrollToBottom(): void {
    this.updatesContainer.nativeElement.scrollTop =
      this.updatesContainer.nativeElement.scrollHeight;
  }

  loadUserPhotos(updates: { userEmail?: string }[]): void {
    const emails = [
      ...new Set(updates.map(u => u.userEmail).filter((e): e is string => !!e)),
    ];
    const uncached = emails.filter(e => !this.photoCache.has(e));
    if (uncached.length === 0) return;

    const photoRequests = uncached.map(email =>
      this._msalApiService.getUserProfilePicture(email).pipe(
        map((blob: Blob) => {
          const safeUrl = this._domSanitizer.bypassSecurityTrustUrl(
            URL.createObjectURL(blob)
          );
          return { email, url: this._domSanitizer.sanitize(4, safeUrl) ?? "" };
        }),
        catchError(() => of({ email, url: "" }))
      )
    );

    forkJoin(photoRequests).subscribe(results => {
      results.forEach(r => this.photoCache.set(r.email, r.url));
    });
  }

  getPhotoUrl(userEmail: string | undefined): string {
    if (!userEmail) return "";
    return this.photoCache.get(userEmail) ?? "";
  }

  convertLinksToAnchorTags(text: string): string {
    const urlRegex = /(?:https?:\/\/|www\.)[^\s/$.?#].[^\s]*/gi;

    return text.replace(urlRegex, match => {
      const href = match.startsWith("www.") ? `http://${match}` : match; // Ensure proper protocol
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    });
  }

  getWorkflowNotesList() {
    if (this.projectWorkflowId !== 0) {
      this._productionWorkflowService
        .getWorkflowNotesList(this.projectWorkflowId)
        .subscribe({
          next: res => {
            this.workflowNotesList = res.data ?? [];
            this.workflowNotesPinnedList = this.workflowNotesList.filter(
              x => x.pinned
            );
            this.loadUserPhotos(this.workflowNotesList);
          },
          error: res => {
            this._toastr.error("Error", res.message);
          },
        });
    } else {
      this.workflowNotesList = [];
      this.workflowNotesPinnedList = [];
    }
  }

  noteError = false;
  isPosting = false;

  getDisplayNote(note: string): string {
    return (note ?? "").replace(/^(?:[ \t]*\r?\n)+/, "");
  }

  formatNote(note: string): string {
    return this.getDisplayNote(note)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\r?\n/g, "<br>");
  }

  hasLineBreak(note: string): boolean {
    return /\r?\n/.test(this.getDisplayNote(note));
  }

  onNoteEditorTextChange(): void {
    this.noteError = false;
  }

  postWorkflowNote() {
    if (!this.stripHtml(this.note).trim()) {
      this.noteError = true;
      return;
    }
    this.noteError = false;
    this.isPosting = true;
    this.showSmalLoaderEvent.emit(true);
    const payload: WorkflowNotesDto = {
      projectWorkFlowId: this.projectWorkflowId,
      note: this.note,
    };
    this._productionWorkflowService.postWorkflowNote(payload).subscribe({
      next: res => {
        if (res?.status !== "Success") {
          this._toastr.error("Error", "Note was not saved");
        } else {
          this.note = "";
          this.getWorkflowNotesList();
        }
        this.isPosting = false;
        this.showSmalLoaderEvent.emit(false);
      },
      error: res => {
        this._toastr.error("Error", res.message);
        this.isPosting = false;
        this.showSmalLoaderEvent.emit(false);
      },
    });
  }

  onNoteChecked(event: CheckboxChangeEvent, recordId: number) {
    const isChecked = event.checked;
    const payload: WorkflowUpdatesPinRequestDto = {
      recordId,
      pinStatus: isChecked,
    };
    this._productionWorkflowService
      .setWorkflowNotePinStatus(payload)
      .subscribe({
        next: res => {
          if (res?.data) {
            this.getWorkflowNotesList();
          }
        },
        error: res => {
          this._toastr.error("Error", res.message);
        },
      });
  }

  onValueChange() {
    this.hasUnsavedChangesEvent.emit(true);
  }

  stripHtml(html: string): string {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent ?? tmp.innerText ?? "";
  }

  onChecked(event: CheckboxChangeEvent, recordId: number) {
    const isChecked = event.checked;
    const payload = {
      recordId: recordId,
      pinStatus: isChecked,
    } as WorkflowUpdatesPinRequestDto;
    this._productionWorkflowService
      .setWorkflowUpdatePinStatus(payload)
      .subscribe({
        next: res => {
          if (res?.data) {
            this.getWorkflowUpdatesList(false);
          }
        },
        error: res => {
          this._toastr.error("Error", res.message);
        },
      });
  }
}
